export const investmentPlanStorageKey = 'finance-investment-plan-v1';

export const planFields = [
  { id: 'goal', label: '這筆錢的用途', prompt: '這筆錢要完成什麼事情？用自己的話寫下目標與優先順序。', maxLength: 1500 },
  { id: 'horizon', label: '預計使用時間', prompt: '什麼時候可能需要用錢？哪些時間已確定，哪些仍有變數？', maxLength: 1500 },
  { id: 'availableMoney', label: '目前可投入的資金', prompt: '記錄你已盤點的資金，以及需要另留作生活支出或其他用途的部分。', maxLength: 1500 },
  { id: 'riskLimits', label: '風險與資金使用限制', prompt: '哪些虧損、資金卡住或生活變化會影響你的安排？尚未確定也可以直接寫下來。', maxLength: 1500 },
  { id: 'allocation', label: '資產配置的想法', prompt: '記錄目前想到的配置與理由，並標示尚未查證的假設；這不是必須下單的清單。', maxLength: 1500 },
  { id: 'contributions', label: '投入方式與頻率', prompt: '資金何時到位？你打算如何安排投入，以及資金不足時如何處理？', maxLength: 1500 },
  { id: 'reviewRules', label: '檢視與調整規則', prompt: '什麼時間或情況需要重新檢視？檢視哪些資料，再決定是否調整？', maxLength: 1500 },
  { id: 'openQuestions', label: '還需要查證的問題', prompt: '把不懂的名詞、商品條件、資料來源與需要補充的資訊留在這裡。', maxLength: 1500 },
];

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const ownValue = (record, key) => Object.getOwnPropertyDescriptor(record, key)?.value;
const compatibleVersion = record => {
  const descriptor = Object.getOwnPropertyDescriptor(record, 'version');
  return !descriptor || descriptor.value === 1;
};

export function createEmptyInvestmentPlan() {
  return Object.fromEntries([['version', 1], ...planFields.map(field => [field.id, ''])]);
}

/** Keep only own string fields; never coerce, invoke getters or merge unknown keys. */
export function normalizeInvestmentPlan(raw) {
  const plan = createEmptyInvestmentPlan();
  if (!isRecord(raw)) return plan;
  try {
    if (!compatibleVersion(raw)) return plan;
    for (const field of planFields) {
      const value = ownValue(raw, field.id);
      if (typeof value === 'string') plan[field.id] = value.slice(0, field.maxLength);
    }
  } catch { return createEmptyInvestmentPlan(); }
  return plan;
}

/** This counts written sections, not the quality, suitability or safety of a plan. */
export function getInvestmentPlanProgress(raw) {
  const plan = normalizeInvestmentPlan(raw);
  const missingIds = planFields.filter(field => !plan[field.id].trim()).map(field => field.id);
  return { filled: planFields.length - missingIds.length, total: planFields.length, missingIds };
}

/**
 * Storage is supplied by the caller, so these helpers also work without a browser.
 * Missing data is normal; unreadable JSON, unknown schema versions or denied access
 * return an empty plan with storageError true. Reading never writes or deletes data.
 */
export function readInvestmentPlan(storage) {
  try {
    if (!storage || typeof storage.getItem !== 'function') throw new Error('Unavailable storage');
    const stored = storage.getItem(investmentPlanStorageKey);
    if (stored === null) return { plan: createEmptyInvestmentPlan(), storageError: false };
    if (typeof stored !== 'string') throw new Error('Invalid stored value');
    const raw = JSON.parse(stored);
    if (!isRecord(raw) || ownValue(raw, 'version') !== 1) throw new Error('Unsupported plan version');
    return { plan: normalizeInvestmentPlan(raw), storageError: false };
  } catch { return { plan: createEmptyInvestmentPlan(), storageError: true }; }
}

/** Save only the normalized v1 document. Invalid schemas do not overwrite storage. */
export function saveInvestmentPlan(storage, raw) {
  try {
    if (!storage || typeof storage.setItem !== 'function' || !isRecord(raw) || !compatibleVersion(raw)) return false;
    storage.setItem(investmentPlanStorageKey, JSON.stringify(normalizeInvestmentPlan(raw)));
    return true;
  } catch { return false; }
}

function exportTimestamp(value) {
  if (value instanceof Date) {
    const time = Date.prototype.getTime.call(value);
    return Number.isFinite(time) ? new Date(time).toISOString() : null;
  }
  if (typeof value !== 'string') return null;
  const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(?:Z|[+-]\d{2}:\d{2})$/);
  if (!parts) return null;
  const [, year, month, day, hour, minute, second] = parts.map(Number);
  // Check calendar components separately: Date.parse would roll February 30 forward.
  const calendar = new Date(0);
  calendar.setUTCFullYear(year, month - 1, day);
  calendar.setUTCHours(hour, minute, second, 0);
  if (calendar.getUTCFullYear() !== year || calendar.getUTCMonth() !== month - 1 || calendar.getUTCDate() !== day
    || calendar.getUTCHours() !== hour || calendar.getUTCMinutes() !== minute || calendar.getUTCSeconds() !== second) return null;
  const time = Date.parse(value);
  return Number.isFinite(time) ? new Date(time).toISOString() : null;
}

const markdownText = value => value.replace(/\r\n?/g, '\n')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/([\\`*_\[\]{}()#+.!|~-])/g, '\\$1')
  .split('\n').map(line => `> ${line}`).join('\n');

/** Export user text as quoted literal Markdown; nothing is interpreted as HTML. */
export function formatInvestmentPlan(raw, options = {}) {
  const plan = normalizeInvestmentPlan(raw);
  const timestamp = exportTimestamp(options?.exportedAt);
  const sections = ['# 我的投資學習計畫'];
  if (timestamp) sections.push(`匯出時間：${timestamp}（UTC）`);
  sections.push('這份筆記用於整理學習與待查證事項，不是投資建議，也不是報酬或風險的保證。');
  for (const field of planFields) {
    sections.push(`## ${field.label}\n\n${markdownText(plan[field.id].trim() || '未填寫')}`);
  }
  return `${sections.join('\n\n')}\n`;
}
